import React, { useState, useEffect, useMemo } from 'react';
import API_BASE_URL from '../../../../components/apiconfig/api-config';
import { FaFilter, FaPrint } from 'react-icons/fa';
import SearchableDropdown from '../../../../components/common/SearchableDropdown';
import NoRecordFound from '../../../../components/common/no-record-found';

const ROWS_PER_PAGE = 50;

const SaleBrokerReport = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [totals, setTotals] = useState({ total_contract_qty: '0.00', total_do_qty: '0.00', total_lifted_qty: '0.00' });
  const [filtersApplied, setFiltersApplied] = useState(false);
  const [showFilters, setShowFilters] = useState(true); // Control filter visibility
  
  // Editable fields state - keyed by sales_contract_no
  const [editableFields, setEditableFields] = useState({});
  
  // Track which field is currently being edited - format: "contractNo_fieldName"
  const [editingField, setEditingField] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    buyer_id: '',
    vessel_name: '',
    sales_contract_no: '',
    broker_id: '',
    period_from: '',
    period_to: ''
  });
  const [showAllRecords, setShowAllRecords] = useState(false);

  // Domestic buyer options
  const [buyers, setBuyers] = useState([]);
  const buyerOptions = useMemo(
    () => buyers.map(buyer => ({ id: buyer.domestic_buyer_id, name: buyer.name })),
    [buyers]
  );

  // Broker options
  const [brokers, setBrokers] = useState([]);
  const brokerOptions = useMemo(
    () => brokers.map(broker => ({ id: broker.broker_id, name: broker.name })),
    [brokers]
  );

  // Sales contract options
  const [salesContracts, setSalesContracts] = useState([]);
  const salesContractOptions = useMemo(
    () => salesContracts.map(contract => ({ id: contract.sales_contract_no, name: contract.sales_contract_no })),
    [salesContracts]
  );

  // Vessel name options
  const [vessels, setVessels] = useState([]);
  const vesselOptions = useMemo(
    () => vessels.map(vessel => ({ id: vessel.vessel_name, name: vessel.vessel_name })),
    [vessels]
  );

  // Fetch domestic buyers list
  useEffect(() => {
    const fetchBuyers = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-domestic-buyers`, {
          credentials: 'include',
        });
        if (res.ok) {
          const result = await res.json();
          setBuyers(result.data || []);
        }
      } catch (err) {
        console.error('Error fetching buyers:', err);
      }
    };
    fetchBuyers();
  }, []);

  // Fetch brokers list
  useEffect(() => {
    const fetchBrokers = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/sale-broker-get?limit=1000`, {
          credentials: 'include',
        });
        if (res.ok) {
          const result = await res.json();
          setBrokers(result.data || []);
        }
      } catch (err) {
        console.error('Error fetching brokers:', err);
      }
    };
    fetchBrokers();
  }, []);

  // Fetch sales contracts list
  useEffect(() => {
    const fetchSalesContracts = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-sales-contracts`, {
          credentials: 'include',
        });
        if (res.ok) {
          const result = await res.json();
          setSalesContracts(result.data || []);
        }
      } catch (err) {
        console.error('Error fetching sales contracts:', err);
      }
    };
    fetchSalesContracts();
  }, []);

  // Fetch vessel names list
  useEffect(() => {
    const fetchVessels = async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/masters/dropdown-vessel-names`, {
          credentials: 'include',
        });
        if (res.ok) {
          const result = await res.json();
          setVessels(result.data || []);
        }
      } catch (err) {
        console.error('Error fetching vessel names:', err);
      }
    };
    fetchVessels();
  }, []);

  // Fetch report data
  const fetchReportData = async () => {
    if (!filtersApplied) {
      return; // Don't fetch if filters not applied
    }

    setLoading(true);
    setError(null);
    try {
      // Build params - only include non-empty values
      const params = new URLSearchParams({
        page,
        limit: ROWS_PER_PAGE,
      });
      
      // If "Show All Records" is checked, don't add any filter params
      if (!showAllRecords) {
        // Handle buyer_id (can be number or string)
        if (filters.buyer_id) {
          const buyerIdValue = typeof filters.buyer_id === 'string' ? filters.buyer_id.trim() : String(filters.buyer_id);
          if (buyerIdValue !== '' && buyerIdValue !== '0') {
            params.append('buyer_id', buyerIdValue);
          }
        }
        
        // Handle vessel_name (string)
        if (filters.vessel_name && typeof filters.vessel_name === 'string' && filters.vessel_name.trim() !== '') {
          params.append('vessel_name', filters.vessel_name.trim());
        }
        
        // Handle sales_contract_no (string)
        if (filters.sales_contract_no && typeof filters.sales_contract_no === 'string' && filters.sales_contract_no.trim() !== '') {
          params.append('sales_contract_no', filters.sales_contract_no.trim());
        }
        
        // Handle broker_id (can be number or string)
        if (filters.broker_id) {
          const brokerIdValue = typeof filters.broker_id === 'string' ? filters.broker_id.trim() : String(filters.broker_id);
          if (brokerIdValue !== '' && brokerIdValue !== '0') {
            params.append('broker_id', brokerIdValue);
          }
        }
        
        // Handle period_from (string date)
        if (filters.period_from && typeof filters.period_from === 'string' && filters.period_from.trim() !== '') {
          params.append('period_from', filters.period_from.trim());
        }
        
        // Handle period_to (string date)
        if (filters.period_to && typeof filters.period_to === 'string' && filters.period_to.trim() !== '') {
          params.append('period_to', filters.period_to.trim());
        }
      }

      console.log('Applied Filters:', {
        buyer_id: filters.buyer_id || 'none',
        buyer_id_type: typeof filters.buyer_id,
        vessel_name: filters.vessel_name || 'none',
        sales_contract_no: filters.sales_contract_no || 'none',
        broker_id: filters.broker_id || 'none',
        broker_id_type: typeof filters.broker_id,
        period_from: filters.period_from || 'none',
        period_to: filters.period_to || 'none'
      });
      console.log('API URL:', `${API_BASE_URL}/api/report/sale-broker-report?${params.toString()}`);

      const res = await fetch(`${API_BASE_URL}/api/report/sale-broker-report?${params.toString()}`, {
        credentials: 'include',
      });

      if (!res.ok) {
        const errorData = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error(errorData.error || `HTTP ${res.status}: Failed to fetch report data`);
      }
      
      const result = await res.json();
      const reportData = result.data || [];
      setData(reportData);
      setTotal(result.total || 0);
      setTotals(result.totals || { total_contract_qty: '0.00', total_do_qty: '0.00', total_lifted_qty: '0.00' });
      
      // Initialize editable fields with data from server (or defaults)
      const initialFields = {};
      reportData.forEach(row => {
        const contractNo = row.sales_contract_no;
        if (contractNo && !initialFields[contractNo]) {
          initialFields[contractNo] = {
            amount_propose_for_payment: row.amount_propose_for_payment ?? row.total_broker_amount ?? 0,
            remarks: row.remarks || '',
            paid_as_on_date: row.paid_as_on_date || ''
          };
        }
      });
      setEditableFields(prev => ({ ...prev, ...initialFields }));
    } catch (err) {
      const errorMessage = err.message || 'Failed to load report data';
      setError(errorMessage);
      console.error('Fetch report error:', err);
      console.error('Error details:', {
        message: err.message,
        stack: err.stack,
        filters: filters
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReportData();
    // eslint-disable-next-line
  }, [page, filters, filtersApplied, showAllRecords]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const applyFilters = () => {
    setFiltersApplied(true);
    setShowFilters(false); // Hide filters after applying
    setPage(1);
  };

  const clearFilters = () => {
    setFilters({
      buyer_id: '',
      vessel_name: '',
      sales_contract_no: '',
      broker_id: '',
      period_from: '',
      period_to: ''
    });
    setShowAllRecords(false);
    setFiltersApplied(false);
    setShowFilters(true); // Show filters when cleared
    setData([]);
    setTotal(0);
    setTotals({ total_contract_qty: '0.00', total_do_qty: '0.00', total_lifted_qty: '0.00' });
    setPage(1);
  };

  const handleShowAllRecords = (checked) => {
    setShowAllRecords(checked);
    if (checked) {
      // Clear all filters when "Show All Records" is checked
      setFilters({
        buyer_id: '',
        vessel_name: '',
        sales_contract_no: '',
        broker_id: '',
        period_from: '',
        period_to: ''
      });
    }
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const totalPages = Math.ceil(total / ROWS_PER_PAGE);
  const handlePrevPage = () => setPage((p) => Math.max(1, p - 1));
  const handleNextPage = () => setPage((p) => Math.min(totalPages, p + 1));

  const handlePrint = () => {
    window.print();
  };

  const handleDoubleClick = (contractNo, fieldName) => {
    const fieldKey = `${contractNo}_${fieldName}`;
    setEditingField(fieldKey);
  };

  const handleFieldBlur = async (contractNo, field, value, salesContractId) => {
    // Stop editing
    setEditingField(null);

    // Get current state before updating
    const currentFields = editableFields[contractNo] || {};
    
    // Prepare updated fields
    const updatedFields = {
      ...currentFields,
      [field]: value
    };

    // Update local state
    setEditableFields(prev => ({
      ...prev,
      [contractNo]: updatedFields
    }));

    // Save to database
    try {
      const payload = {
        sales_contract_id: salesContractId,
        amount_propose_for_payment: updatedFields.amount_propose_for_payment !== undefined ? updatedFields.amount_propose_for_payment : null,
        remarks: updatedFields.remarks !== undefined ? updatedFields.remarks : null,
        paid_as_on_date: updatedFields.paid_as_on_date || null
      };

      console.log('Saving payment details:', payload);

      const res = await fetch(`${API_BASE_URL}/api/report/sale-broker-payment`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify(payload)
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Failed to save payment details');
      }

      const result = await res.json();
      console.log('Payment details saved successfully:', result);
    } catch (err) {
      console.error('Error saving payment details:', err);
      alert('Failed to save payment details: ' + err.message);
    }
  };

  const handleFieldKeyDown = (e, contractNo, field, value, salesContractId) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleFieldBlur(contractNo, field, value, salesContractId);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      setEditingField(null);
    }
  };

  // Get applied filters with display names
  const getAppliedFilters = () => {
    const applied = [];
    
    if (showAllRecords) {
      applied.push({ label: 'All Records', value: 'Enabled' });
      return applied;
    }
    
    if (filters.buyer_id) {
      const buyer = buyers.find(b => b.domestic_buyer_id === parseInt(filters.buyer_id));
      if (buyer) {
        applied.push({ label: 'Buyer', value: buyer.name });
      }
    }
    
    if (filters.vessel_name) {
      applied.push({ label: 'Vessel Name', value: filters.vessel_name });
    }
    
    if (filters.sales_contract_no) {
      applied.push({ label: 'Sale Contract No', value: filters.sales_contract_no });
    }
    
    if (filters.broker_id) {
      const broker = brokers.find(b => b.broker_id === parseInt(filters.broker_id));
      if (broker) {
        applied.push({ label: 'Broker', value: broker.name });
      }
    }
    
    if (filters.period_from) {
      applied.push({ label: 'Period From', value: filters.period_from });
    }
    
    if (filters.period_to) {
      applied.push({ label: 'Period To', value: filters.period_to });
    }
    
    return applied;
  };

  const appliedFilters = filtersApplied ? getAppliedFilters() : [];

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: legal landscape;
            margin: 0.2in 0.25in;
          }
          
          body {
            margin: 0;
            padding: 0;
          }
          
          /* Hide non-essential elements */
          button,
          .no-print,
          nav,
          .pagination-controls {
            display: none !important;
          }
          
          /* Show only report content */
          .print-container {
            display: block !important;
            width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            overflow: visible !important;
          }
          
          /* Table container styling for print */
          .overflow-x-auto {
            overflow: visible !important;
            border: none !important;
            border-radius: 0 !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 0 !important;
            width: 100% !important;
          }
          
          /* Table styling for print */
          table {
            width: 100% !important;
            border-collapse: collapse !important;
            font-size: 8pt !important;
            page-break-inside: auto !important;
            margin: 0 !important;
            border: 1px solid #000 !important;
            table-layout: auto !important;
          }
          
          thead {
            display: table-header-group !important;
            background-color: #f3f4f6 !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          tbody {
            display: table-row-group !important;
          }
          
          tfoot {
            display: table-footer-group !important;
            background-color: #e5e7eb !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
          
          tr {
            page-break-inside: avoid !important;
            page-break-after: auto !important;
          }
          
          th, td {
            padding: 3px 4px !important;
            border: 1px solid #000 !important;
            text-align: left !important;
            vertical-align: top !important;
            white-space: normal !important;
            word-wrap: break-word !important;
            line-height: 1.3 !important;
            overflow: hidden !important;
          }
          
          th {
            background-color: #f3f4f6 !important;
            font-weight: bold !important;
            font-size: 7.5pt !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            padding: 4px 4px !important;
          }
          
          td {
            font-size: 7.5pt !important;
          }
          
          /* Right align for numeric columns */
          td[style*="text-align: right"],
          th[style*="text-align: right"] {
            text-align: right !important;
          }
          
          /* Ensure table fits page width */
          table {
            max-width: 100% !important;
            width: 100% !important;
          }
          
          /* Optimize text wrapping for long content */
          th, td {
            overflow: hidden !important;
            text-overflow: ellipsis !important;
          }
          
          /* Print header */
          .print-header {
            display: block !important;
            text-align: center !important;
            font-size: 12pt !important;
            font-weight: bold !important;
            margin-bottom: 8px !important;
            page-break-after: avoid !important;
          }
          
          .print-header.hidden {
            display: block !important;
          }
          
          /* Print filters */
          .print-filters {
            display: block !important;
            font-size: 8pt !important;
            margin-bottom: 8px !important;
            page-break-after: avoid !important;
          }
          
          .print-filters.hidden {
            display: block !important;
          }
          
          /* Ensure proper page breaks */
          .page-break {
            page-break-before: always !important;
          }
          
          /* Hide scrollbars */
          * {
            overflow: visible !important;
          }
          
          /* Container adjustments */
          .h-\\[calc\\(100vh-4rem-1rem\\)\\] {
            height: auto !important;
          }
          
          .overflow-y-auto {
            overflow: visible !important;
          }
          
          /* Remove padding from container */
          .p-4 {
            padding: 0 !important;
          }
          
          .mb-4 {
            margin-bottom: 0 !important;
          }
          
          /* Ensure proper spacing */
          .flex-1 {
            flex: none !important;
          }
          
          .max-w-8xl {
            max-width: 100% !important;
          }
          
          .mx-auto {
            margin-left: 0 !important;
            margin-right: 0 !important;
          }
        }
      `}</style>
      
    <div className="h-[calc(100vh-4rem-1rem)] flex flex-col bg-white -m-2 print-container">
      {/* Header */}
      <div className="flex-shrink-0 flex flex-col sm:flex-row justify-between items-start sm:items-center p-4 gap-3 border-b border-gray-200 no-print">
        <h2 className="text-xl font-semibold">Sale Broker Report</h2>
        
        {/* Applied Filters Indication */}
        {filtersApplied && appliedFilters.length > 0 && (
          <div className="flex-1 flex flex-wrap items-center gap-2 px-4">
            <span className="text-sm text-gray-600 font-medium">Applied Filters:</span>
            {appliedFilters.map((filter, index) => (
              <span
                key={index}
                className="inline-flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-800 rounded border border-blue-200"
              >
                <span className="font-medium">{filter.label}:</span>
                <span>{filter.value}</span>
              </span>
            ))}
          </div>
        )}
        
        <div className="flex items-center gap-2">
          {filtersApplied && (
            <>
              <button
                onClick={toggleFilters}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-gray-600 text-white rounded hover:bg-gray-700"
              >
                <FaFilter />
                {showFilters ? 'Hide Filters' : 'Show Filters'}
              </button>
              <button
                onClick={handlePrint}
                className="flex items-center gap-1 px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                <FaPrint />
                Print
              </button>
            </>
          )}
        </div>
      </div>

      {/* Filters Section */}
      {showFilters && (
        <div className="flex items-center justify-center flex-1 p-4 overflow-y-auto">
          <div className="bg-gray-50 p-8 rounded-lg border border-gray-200 shadow-lg w-full max-w-4xl">
          <h3 className="text-lg font-semibold mb-4 text-center text-gray-700">Filter Report</h3>
          
          {/* Show All Records Checkbox */}
          <div className="mb-6 flex items-center justify-center">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={showAllRecords}
                onChange={(e) => handleShowAllRecords(e.target.checked)}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <span className="text-sm font-medium text-gray-700">Show All Records (Without Filters)</span>
            </label>
          </div>

          <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 ${showAllRecords ? 'opacity-50 pointer-events-none' : ''}`}>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Buyer</label>
              <SearchableDropdown
                options={buyerOptions}
                value={filters.buyer_id}
                onChange={(value) => handleFilterChange('buyer_id', value)}
                placeholder="Select Buyer"
                displayKey="name"
                valueKey="id"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Vessel Name</label>
              <SearchableDropdown
                options={vesselOptions}
                value={filters.vessel_name}
                onChange={(value) => handleFilterChange('vessel_name', value)}
                placeholder="Select Vessel Name"
                displayKey="name"
                valueKey="id"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Sale Contract No</label>
              <SearchableDropdown
                options={salesContractOptions}
                value={filters.sales_contract_no}
                onChange={(value) => handleFilterChange('sales_contract_no', value)}
                placeholder="Select Sale Contract No"
                displayKey="name"
                valueKey="id"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Broker Name</label>
              <SearchableDropdown
                options={brokerOptions}
                value={filters.broker_id}
                onChange={(value) => handleFilterChange('broker_id', value)}
                placeholder="Select Broker"
                displayKey="name"
                valueKey="id"
                className="w-full"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Period From</label>
              <input
                type="date"
                value={filters.period_from}
                onChange={(e) => handleFilterChange('period_from', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">Period To</label>
              <input
                type="date"
                value={filters.period_to}
                onChange={(e) => handleFilterChange('period_to', e.target.value)}
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
          <div className="mt-6 flex justify-center gap-3">
            <button
              onClick={applyFilters}
              className="px-6 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Apply Filters
            </button>
            {filtersApplied && (
              <button
                onClick={clearFilters}
                className="px-6 py-2 text-sm font-medium bg-gray-300 text-gray-700 rounded hover:bg-gray-400 focus:outline-none focus:ring-2 focus:ring-gray-500"
              >
                Clear Filters
              </button>
            )}
          </div>
        </div>
        </div>
      )}
      
      {/* Report Table - Show when filters are applied and filters are hidden */}
      {filtersApplied && !showFilters && (
        <div className="flex-1 overflow-y-auto p-4 max-w-8xl mx-auto w-full">
          {/* Report Table - Only show when filters are applied */}
          {loading ? (
            <div className="text-center py-8 text-blue-600">Loading...</div>
          ) : error ? (
            <div className="text-center py-8 text-red-600">{error}</div>
          ) : data.length === 0 ? (
            <NoRecordFound message="No report data found for the selected filters." />
          ) : (
            <>
              <div className="overflow-x-auto rounded border border-gray-200 mb-4">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100">
                <tr>
                  <th className="px-3 py-2 text-left font-medium border-r border-gray-300">Sale Contract No</th>
                  <th className="px-3 py-2 text-left font-medium border-r border-gray-300">Name of Party</th>
                  <th className="px-3 py-2 text-right font-medium border-r border-gray-300">Contract QTY</th>
                  <th className="px-3 py-2 text-right font-medium border-r border-gray-300">Rate</th>
                  <th className="px-3 py-2 text-left font-medium border-r border-gray-300">DO No</th>
                  <th className="px-3 py-2 text-left font-medium border-r border-gray-300">DO Date</th>
                  <th className="px-3 py-2 text-right font-medium border-r border-gray-300">DO QTY</th>
                  <th className="px-3 py-2 text-left font-medium border-r border-gray-300">Sale Date</th>
                  <th className="px-3 py-2 text-right font-medium border-r border-gray-300">Lifted QTY</th>
                  <th className="px-3 py-2 text-right font-medium border-r border-gray-300">Broker Rate</th>
                  <th className="px-3 py-2 text-right font-medium border-r border-gray-300">Total Broker Amount</th>
                  <th className="px-3 py-2 text-right font-medium border-r border-gray-300">Amount Propose for Payment</th>
                  <th className="px-3 py-2 text-left font-medium border-r border-gray-300">Remarks</th>
                  <th className="px-3 py-2 text-left font-medium">Paid As on Date</th>
                  </tr>
                  </thead>
                  <tbody>
                {(() => {
                  // Group data by sales_contract_no and then by DO, preserving order
                  // Use a Set to track unique rows to prevent duplicates
                  const seenRows = new Set();
                  const contractGroups = [];
                  const contractMap = new Map();
                  
                  data.forEach((row) => {
                    // Create unique key: sale_id if exists, otherwise use do_id
                    const uniqueKey = row.sale_id 
                      ? `so_${row.sale_id}` 
                      : `do_${row.do_id}_${row.sales_contract_id}`;
                    
                    // Skip if we've already seen this row
                    if (seenRows.has(uniqueKey)) {
                      return;
                    }
                    seenRows.add(uniqueKey);
                    
                    const contractNo = row.sales_contract_no || 'UNKNOWN';
                    const doId = row.do_id || 'UNKNOWN_DO';
                    
                    if (!contractMap.has(contractNo)) {
                      const contractGroup = {
                        contractNo,
                        doGroups: []
                      };
                      contractGroups.push(contractGroup);
                      contractMap.set(contractNo, contractGroup);
                    }
                    
                    const contractGroup = contractMap.get(contractNo);
                    let doGroup = contractGroup.doGroups.find(dg => dg.doId === doId);
                    
                    if (!doGroup) {
                      doGroup = {
                        doId,
                        rows: []
                      };
                      contractGroup.doGroups.push(doGroup);
                    }
                    
                    doGroup.rows.push(row);
                  });

                  // Flatten grouped data with rowSpan information
                  const flattenedData = [];
                  contractGroups.forEach((contractGroup) => {
                    contractGroup.doGroups.forEach((doGroup, doGroupIdx) => {
                      doGroup.rows.forEach((row, salesOrderIdx) => {
                        flattenedData.push({
                          ...row,
                          isFirstContractRow: doGroupIdx === 0 && salesOrderIdx === 0,
                          isFirstDoRow: salesOrderIdx === 0,
                          contractRowSpan: contractGroup.doGroups.reduce((sum, dg) => sum + dg.rows.length, 0),
                          doRowSpan: doGroup.rows.length,
                          contractGroup: contractGroup.contractNo,
                          doGroup: doGroup.doId
                        });
                      });
                    });
                  });

                  return flattenedData.map((row, localIdx) => {
                    const isContractStart = row.isFirstContractRow;
                    const isDoStart = row.isFirstDoRow;
                    const prevRow = localIdx > 0 ? flattenedData[localIdx - 1] : null;
                    const nextRow = localIdx < flattenedData.length - 1 ? flattenedData[localIdx + 1] : null;
                    const isLastInContract = !nextRow || nextRow.contractGroup !== row.contractGroup;
                    const isNewContract = !prevRow || prevRow.contractGroup !== row.contractGroup;
                    
                    // Alternate row colors based on contract group, not individual rows
                    const contractGroupIndex = contractGroups.findIndex(g => g.contractNo === row.contractGroup);
                    const isEvenGroup = contractGroupIndex % 2 === 0;
                    
                    return (
                      <tr 
                        key={`${row.contractGroup}-${row.doGroup}-${localIdx}`} 
                        className={`
                          ${isEvenGroup ? 'bg-white' : 'bg-gray-50'}
                          ${isContractStart && isNewContract ? 'border-t-2 border-blue-200' : ''}
                          ${isLastInContract ? 'border-b-2 border-blue-200' : ''}
                        `}
                      >
                        {isContractStart ? (
                          <>
                            <td 
                              className="px-3 py-2 border-r border-gray-200 font-semibold bg-white align-top" 
                              rowSpan={row.contractRowSpan}
                            >
                              <div className="leading-tight">
                                <div className="whitespace-nowrap">{row.sales_contract_no || '-'}</div>
                                {row.contract_date && (
                                  <div className="text-xs text-gray-600 font-normal mt-1">Contract Date: {row.contract_date}</div>
                                )}
                              </div>
                            </td>
                            <td 
                              className="px-3 py-2 border-r border-gray-200 bg-white align-top" 
                              rowSpan={row.contractRowSpan}
                            >
                              {row.domestic_buyer_name || '-'}
                            </td>
                            <td 
                              className="px-3 py-2 text-right border-r border-gray-200 bg-white align-top" 
                              rowSpan={row.contractRowSpan}
                            >
                              {row.contract_qty || '0.00'}
                            </td>
                            <td 
                              className="px-3 py-2 text-right border-r border-gray-200 bg-white align-top" 
                              rowSpan={row.contractRowSpan}
                            >
                              {row.rate || '-'}
                            </td>
                          </>
                        ) : null}
                        {isDoStart ? (
                          <>
                            <td 
                              className="px-3 py-2 border-r border-gray-200 bg-white align-top" 
                              rowSpan={row.doRowSpan}
                            >
                              {row.do_no || '-'}
                            </td>
                            <td 
                              className="px-3 py-2 border-r border-gray-200 bg-white align-top" 
                              rowSpan={row.doRowSpan}
                            >
                              {row.do_date || '-'}
                            </td>
                            <td 
                              className="px-3 py-2 text-right border-r border-gray-200 bg-white align-top" 
                              rowSpan={row.doRowSpan}
                            >
                              {row.do_qty || '0.00'}
                            </td>
                          </>
                        ) : null}
                        <td className="px-3 py-2 border-r border-gray-200">{row.sale_date || '-'}</td>
                        <td className="px-3 py-2 text-right border-r border-gray-200">{row.lifted_qty || '0.00'}</td>
                        <td className="px-3 py-2 text-right border-r border-gray-200">
                          {row.broker_rate ? row.broker_rate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                        </td>
                        {isContractStart ? (
                          <>
                            <td 
                              className="px-3 py-2 text-right bg-white align-top font-medium border-r border-gray-200" 
                              rowSpan={row.contractRowSpan}
                            >
                              {row.total_broker_amount ? row.total_broker_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}
                            </td>
                            <td 
                              className="px-3 py-2 bg-white align-top border-r border-gray-200 cursor-pointer" 
                              rowSpan={row.contractRowSpan}
                              onDoubleClick={() => handleDoubleClick(row.contractGroup, 'amount_propose_for_payment')}
                            >
                              {editingField === `${row.contractGroup}_amount_propose_for_payment` ? (
                                <input
                                  type="number"
                                  step="0.01"
                                  className="w-full px-2 py-1 text-right border border-blue-500 rounded text-sm"
                                  value={editableFields[row.contractGroup]?.amount_propose_for_payment ?? row.total_broker_amount ?? 0}
                                  onChange={(e) => {
                                    const value = parseFloat(e.target.value) || 0;
                                    setEditableFields(prev => ({
                                      ...prev,
                                      [row.contractGroup]: {
                                        ...prev[row.contractGroup],
                                        amount_propose_for_payment: value
                                      }
                                    }));
                                  }}
                                  onBlur={(e) => handleFieldBlur(row.contractGroup, 'amount_propose_for_payment', parseFloat(e.target.value) || 0, row.sales_contract_id)}
                                  onKeyDown={(e) => handleFieldKeyDown(e, row.contractGroup, 'amount_propose_for_payment', parseFloat(e.target.value) || 0, row.sales_contract_id)}
                                  autoFocus
                                />
                              ) : (
                                <div className="text-right">
                                  {editableFields[row.contractGroup]?.amount_propose_for_payment !== undefined 
                                    ? editableFields[row.contractGroup].amount_propose_for_payment.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                                    : (row.total_broker_amount ? row.total_broker_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00')}
                                </div>
                              )}
                            </td>
                            <td 
                              className="px-3 py-2 bg-white align-top border-r border-gray-200 cursor-pointer" 
                              rowSpan={row.rowSpan}
                              onDoubleClick={() => handleDoubleClick(row.contractGroup, 'remarks')}
                            >
                              {editingField === `${row.contractGroup}_remarks` ? (
                                <input
                                  type="text"
                                  className="w-full px-2 py-1 border border-blue-500 rounded text-sm"
                                  value={editableFields[row.contractGroup]?.remarks || ''}
                                  onChange={(e) => {
                                    setEditableFields(prev => ({
                                      ...prev,
                                      [row.contractGroup]: {
                                        ...prev[row.contractGroup],
                                        remarks: e.target.value
                                      }
                                    }));
                                  }}
                                  onBlur={(e) => handleFieldBlur(row.contractGroup, 'remarks', e.target.value, row.sales_contract_id)}
                                  onKeyDown={(e) => handleFieldKeyDown(e, row.contractGroup, 'remarks', e.target.value, row.sales_contract_id)}
                                  placeholder="Enter remarks"
                                  autoFocus
                                />
                              ) : (
                                <div className="min-h-[20px]">
                                  {editableFields[row.contractGroup]?.remarks || '-'}
                                </div>
                              )}
                            </td>
                            <td 
                              className="px-3 py-2 bg-white align-top cursor-pointer" 
                              rowSpan={row.contractRowSpan}
                              onDoubleClick={() => handleDoubleClick(row.contractGroup, 'paid_as_on_date')}
                            >
                              {editingField === `${row.contractGroup}_paid_as_on_date` ? (
                                <input
                                  type="date"
                                  className="w-full px-2 py-1 border border-blue-500 rounded text-sm"
                                  value={editableFields[row.contractGroup]?.paid_as_on_date || ''}
                                  onChange={(e) => {
                                    setEditableFields(prev => ({
                                      ...prev,
                                      [row.contractGroup]: {
                                        ...prev[row.contractGroup],
                                        paid_as_on_date: e.target.value
                                      }
                                    }));
                                  }}
                                  onBlur={(e) => handleFieldBlur(row.contractGroup, 'paid_as_on_date', e.target.value, row.sales_contract_id)}
                                  onKeyDown={(e) => handleFieldKeyDown(e, row.contractGroup, 'paid_as_on_date', e.target.value, row.sales_contract_id)}
                                  autoFocus
                                />
                              ) : (
                                <div>
                                  {editableFields[row.contractGroup]?.paid_as_on_date || '-'}
                                </div>
                              )}
                            </td>
                          </>
                        ) : null}
                      </tr>
                    );
                  });
                })()}
                  </tbody>
                  <tfoot className="bg-gray-200 font-semibold">
                <tr>
                  <td colSpan="2" className="px-3 py-2 text-right border-r border-gray-300">Total:</td>
                  <td className="px-3 py-2 text-right border-r border-gray-300">{totals.total_contract_qty || '0.00'}</td>
                  <td colSpan="3" className="px-3 py-2 border-r border-gray-300"></td>
                  <td className="px-3 py-2 text-right border-r border-gray-300">{totals.total_do_qty || '0.00'}</td>
                  <td className="px-3 py-2 border-r border-gray-300"></td>
                  <td className="px-3 py-2 text-right border-r border-gray-300">{totals.total_lifted_qty || '0.00'}</td>
                  <td className="px-3 py-2 text-right border-r border-gray-300">{totals.total_broker_rate ? totals.total_broker_rate.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</td>
                  <td className="px-3 py-2 text-right border-r border-gray-300">{totals.total_broker_amount ? totals.total_broker_amount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'}</td>
                  <td className="px-3 py-2 text-right border-r border-gray-300">
                    {Object.values(editableFields).reduce((sum, field) => sum + (parseFloat(field?.amount_propose_for_payment) || 0), 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </td>
                  <td className="px-3 py-2 border-r border-gray-300"></td>
                  <td className="px-3 py-2"></td>
                  </tr>
                  </tfoot>
                </table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex justify-end items-center gap-2 text-sm pagination-controls">
              <button
                onClick={handlePrevPage}
                disabled={page === 1}
                className={`px-3 py-1 rounded border ${
                  page === 1
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'
                }`}
              >
                Prev
              </button>
              <span className="text-gray-600">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={handleNextPage}
                disabled={page === totalPages}
                className={`px-3 py-1 rounded border ${
                  page === totalPages
                    ? 'bg-gray-100 text-gray-400 border-gray-200 cursor-not-allowed'
                    : 'bg-white text-blue-600 border-blue-600 hover:bg-blue-50'
                }`}
              >
                  Next
                </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
    </>
  );
};

export default SaleBrokerReport;
